import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useOffer } from './OfferCardContext';
import { prepareOfferUrl } from './utils/prepareOfferUrl';
import { getDateRangeValue, getRangeValue } from '../../../../packages/JsonQuery';
import {
  trackAddFavorite,
  trackBookClick,
  trackCall,
  trackCardClick,
  trackChatClick,
  trackShowPhoneClick,
  trackSlideChange,
} from '../../../ListingMobile';
import { OfferCard } from '../../../ListingMobile/components';
import { trackBuildingClick } from '../../../ListingMobile/tracking/trackBuildingClick';
import { trackMoreClick } from '../../../ListingMobile/tracking/trackMoreClick';
import { trackOfferCardShow } from '../../../ListingMobile/tracking/trackOfferCardShow';
import { trackRemoveFromFavorite } from '../../../ListingMobile/tracking/trackRemoveFromFavorite';
import { useIsOfferPreviewEnabled } from '../../../ListingMobile/utils/useIsOfferPreviewEnabled';
import { onAddToFavorite as addToFavorites } from '../../actions/newFavorites/onAddToFavorite';
import { removeFromFavorites } from '../../actions/newFavorites/removeFromFavorite';
import { useTrackingProps } from '../../hooks/useTrackingProps';
import { selectListingMlRanking } from '../../selectors/listingOffers/selectListingMlRanking';
import { selectResultsJsonQuery } from '../../selectors/results';
import { TThunkDispatch } from '../../types/redux';
import { defferedTrack } from '../../utils/tracking';
import { useApplicationContext } from '../ApplicationContext';

export const OfferCardContainer = () => {
  const { offer, offerPosition, offersLength } = useOffer();
  const { config } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const mlRanking = useSelector(selectListingMlRanking);
  const isSliderEventTriggeredRef = React.useRef(false);
  const isOfferPreviewEnabled = useIsOfferPreviewEnabled();
  const isPartnerFavoritesEnabled = !!config.get<boolean>('dailyrent.partners_favorites.Enabled');
  const jsonQueryDates = getDateRangeValue('dates')(jsonQuery);
  const checkInDate = jsonQueryDates?.gte;
  const checkOutDate = jsonQueryDates?.lt;
  const minBeds = getRangeValue('beds_count')(jsonQuery)?.gte;
  const isFavoriteButtonVisible = (!offer.isCianPartner || isPartnerFavoritesEnabled) && !offer.isHotel;
  const hasDates = !!jsonQueryDates;

  const offerLink = prepareOfferUrl({
    offer,
    checkInDate,
    checkOutDate,
    minBeds,
  });
  const trackingProps = useTrackingProps({ offer, offerPosition, offersLength });

  const onCardClick = React.useCallback(() => {
    trackCardClick(trackingProps);
  }, [trackingProps]);

  const onMoreClick = React.useCallback(() => {
    trackMoreClick(trackingProps);
  }, [trackingProps]);

  const onBookClick = React.useCallback(() => {
    trackBookClick(trackingProps);
  }, [trackingProps]);

  const onCallClick = React.useCallback(() => {
    trackShowPhoneClick(trackingProps);
    defferedTrack(() => trackCall(trackingProps));
  }, [trackingProps]);

  const onChatClick = React.useCallback(() => {
    trackChatClick(trackingProps);
  }, [trackingProps]);

  const onSlideChange = React.useCallback(() => {
    if (!isSliderEventTriggeredRef.current) {
      trackSlideChange(trackingProps);
      isSliderEventTriggeredRef.current = true;
    }
  }, [trackingProps]);

  const onBuildingClick = React.useCallback(() => {
    trackBuildingClick(trackingProps);
  }, [trackingProps]);

  const onAddToFavorites = React.useCallback(() => {
    dispatch(addToFavorites(offer.id));
    trackAddFavorite(trackingProps);
  }, [dispatch, offer.id, trackingProps]);

  const onRemoveFromFavorites = React.useCallback(() => {
    dispatch(removeFromFavorites(offer.id));
    trackRemoveFromFavorite(trackingProps);
  }, [dispatch, offer.id, trackingProps]);

  const onCardShow = () => {
    trackOfferCardShow(trackingProps, mlRanking);
  };

  return (
    <ActionAfterViewObserver percentVisible={0.5} callback={onCardShow} triggerOnce={false} callbackDelay={1000}>
      <OfferCard
        offer={offer}
        hasDates={hasDates}
        // Ранее отображался для EBrandingLevel.ThirdLevel и партнеров
        showBrandingBlock={false}
        showThumbnails={!isOfferPreviewEnabled}
        showFavoriteButton={isFavoriteButtonVisible}
        isSingle={isOfferPreviewEnabled}
        offerLink={offerLink}
        onCardClick={onCardClick}
        onMoreClick={onMoreClick}
        onBookClick={onBookClick}
        onCallClick={onCallClick}
        onChatClick={onChatClick}
        onSlideChange={onSlideChange}
        onBuildingClick={onBuildingClick}
        onAddToFavorites={onAddToFavorites}
        onRemoveFromFavorites={onRemoveFromFavorites}
      />
    </ActionAfterViewObserver>
  );
};
